import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router";
import { Clock, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { foldForSearch } from "@novemai/i18n-sdk";
import { Button, Empty, ErrorState, FilterPillBar, Loading, Modal, ModalBody, ModalHeader, PageHeader, SearchInput, type FilterPillItem } from "@novemai/components";
import { useTabTitle } from "@/components/TabStrip";
import { menuLinks } from "@/components/menu-config";
import { useVisibleMenu } from "@/components/use-visible-menu";
import { useSessionScope } from "@/auth/use-session-scope";
import { useUiStore } from "@/store/ui";
import { searchRecords } from "./api";

const MIN_QUERY = 2;
const DEBOUNCE_MS = 250;
const RECENT_LIMIT = 5;
// The menu's own pages ride in the same result list under this kind; the server
// never returns it, so it cannot collide with an entity_type.
const PAGES = "pages";
const ALL = "all";

interface Hit { readonly id: string; readonly title: string; readonly snippet: string; readonly url: string }
interface ResultGroup { readonly kind: string; readonly hits: readonly Hit[] }

/**
 * Recent queries live in sessionStorage, keyed by the signed-in session: a
 * query can be a colleague's name, so it must not outlive the tab or surface
 * for the next person who signs in on a shared machine.
 */
function recentStorageKey(scope: readonly string[]): string {
  return `novemai.global-search.recent:${JSON.stringify(scope)}`;
}
function readRecent(key: string): readonly string[] {
  try {
    const parsed: unknown = JSON.parse(window.sessionStorage.getItem(key) ?? "[]");
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string").slice(0, RECENT_LIMIT) : [];
  } catch {
    return [];
  }
}
function writeRecent(key: string, values: readonly string[]): void {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(values));
  } catch {
    /* storage unavailable: recent searches simply do not persist */
  }
}

/** Marks the first case-insensitive match; leaves text untouched when case folding changes its length. */
function highlight(text: string, query: string): ReactNode {
  const lower = text.toLocaleLowerCase();
  const needle = query.toLocaleLowerCase();
  const at = lower.length === text.length ? lower.indexOf(needle) : -1;
  if (needle.length === 0 || at === -1) return text;
  return <>{text.slice(0, at)}<mark className="rounded-[2px] bg-[var(--accent-subtle-bg)] px-0.5 font-semibold text-inherit">{text.slice(at, at + needle.length)}</mark>{text.slice(at + needle.length)}</>;
}

function useDebounced(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => { setDebounced(value); }, delayMs);
    return () => { window.clearTimeout(timer); };
  }, [value, delayMs]);
  return debounced;
}

function RecordSearch({ initialQuery = "", initialSelected = "", onNavigate }: { initialQuery?: string; initialSelected?: string; onNavigate?: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const scope = useSessionScope();
  const storageKey = recentStorageKey(scope);
  const { sections } = useVisibleMenu();
  const [text, setText] = useState(initialQuery);
  const [category, setCategory] = useState<string>(ALL);
  const [activeId, setActiveId] = useState(initialSelected);
  const [recent, setRecent] = useState<readonly string[]>(() => readRecent(storageKey));
  const inputRef = useRef<HTMLInputElement>(null);
  const trimmed = text.trim();
  const debounced = useDebounced(trimmed, DEBOUNCE_MS).slice(0, 120);
  const term = debounced.length >= MIN_QUERY ? debounced : "";
  // The key carries the term, so a slower response for an older term lands in
  // its own cache entry and can never replace the current one; React Query
  // aborts the superseded request through `signal`.
  const result = useQuery({ queryKey: ["global-search", ...scope, term], queryFn: ({ signal }) => searchRecords(term, signal), enabled: term !== "", staleTime: 0 });

  const pageHits = useMemo((): readonly Hit[] => {
    const folded = foldForSearch(term);
    if (folded === "") return [];
    return sections.flatMap((section) => menuLinks(section.items))
      .filter((link) => foldForSearch(t(link.labelKey)).includes(folded))
      .map((link) => ({ id: link.key, title: t(link.labelKey), snippet: "", url: link.href }));
  }, [sections, term, t]);

  const groups = useMemo((): readonly ResultGroup[] => {
    const server = (result.data?.groups ?? []).map((group): ResultGroup => ({
      kind: group.entity_type,
      hits: group.entity_type === "documents" ? group.hits.map((hit) => ({ ...hit, snippet: t("global_search.types.documents") })) : group.hits,
    }));
    return [{ kind: PAGES, hits: pageHits }, ...server].filter((group) => group.hits.length > 0);
  }, [result.data, pageHits, t]);

  // A filter left on a category the new term no longer returns would hide every result.
  const effectiveCategory = groups.some((group) => group.kind === category) ? category : ALL;
  const shown = effectiveCategory === ALL ? groups : groups.filter((group) => group.kind === effectiveCategory);
  const options = shown.flatMap((group) => group.hits.map((hit) => ({ ...hit, kind: group.kind, optionId: `global-search-option-${group.kind}-${hit.id}` })));
  const active = options.find((option) => option.id === activeId) ?? options[0];
  const total = groups.reduce((sum, group) => sum + group.hits.length, 0);
  const waiting = trimmed.length >= MIN_QUERY && (trimmed.slice(0, 120) !== debounced || (term !== "" && result.isPending));

  const remember = (query: string): void => {
    const next = [query, ...recent.filter((value) => value !== query)].slice(0, RECENT_LIMIT);
    setRecent(next);
    writeRecent(storageKey, next);
  };
  // Documents have no page of their own yet; their preview IS the destination.
  const opensPage = (url: string): boolean => !url.startsWith("/search?");
  const open = (hit: Hit): void => {
    if (term !== "") remember(term);
    if (!opensPage(hit.url)) return;
    onNavigate?.();
    void navigate(hit.url);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (options.length === 0) return;
    const index = active === undefined ? -1 : options.indexOf(active);
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActiveId(options[(index + step + options.length) % options.length]!.id);
    } else if (event.key === "Enter" && active !== undefined) {
      event.preventDefault();
      open(active);
    }
  };

  const chips: ReadonlyArray<FilterPillItem> = [
    { value: ALL, label: t("global_search.all"), count: total },
    ...groups.map((group) => ({ value: group.kind, label: t(`global_search.types.${group.kind}`), count: group.hits.length })),
  ];

  return <div className="space-y-4" data-tour="global-search">
    <SearchInput
      ref={inputRef}
      className="min-w-0"
      role="combobox"
      aria-label={t("global_search.query")}
      aria-expanded={options.length > 0}
      aria-controls="global-search-results"
      aria-autocomplete="list"
      aria-activedescendant={active?.optionId}
      placeholder={t("global_search.placeholder")}
      value={text}
      maxLength={120}
      icon={<Search size={16} />}
      onChange={(event) => { setText(event.target.value); setActiveId(""); }}
      onKeyDown={onKeyDown}
    />
    {trimmed === "" && recent.length > 0 && <div className="flex flex-wrap items-center gap-2 text-sm text-[var(--fg-secondary)]">
      <Clock size={14} aria-hidden="true" />
      <span>{t("global_search.recent")}</span>
      {recent.map((value) => <button key={value} type="button" onClick={() => { setText(value); inputRef.current?.focus(); }}
        className="rounded-full border border-[var(--border-default)] bg-[var(--bg-surface)] px-2.5 py-0.5 text-xs font-medium text-[var(--fg-primary)] hover:bg-[var(--bg-subtle)] focus-visible:outline-2 focus-visible:outline-[var(--focus-ring)]">{value}</button>)}
    </div>}
    {groups.length > 0 && <FilterPillBar aria-label={t("global_search.categories")} items={chips} value={effectiveCategory} onChange={(value) => { setCategory(value); setActiveId(""); }} />}
    {trimmed.length < MIN_QUERY ? <Empty title={t("global_search.prompt")} description={t("global_search.prompt_hint")} />
      : waiting && groups.length === 0 ? <Loading label={t("global_search.loading")} />
      : result.isError && groups.length === 0 ? <ErrorState title={t("global_search.error")} action={<Button variant="secondary" onClick={() => { void result.refetch(); }}>{t("global_search.retry")}</Button>} />
      : options.length === 0 ? <Empty title={t("global_search.empty")} description={t("global_search.empty_hint")} />
      : <div className="grid min-w-0 gap-4 md:grid-cols-2">
        <div className="min-w-0 space-y-4">
          <div id="global-search-results" role="listbox" aria-label={t("global_search.results")} aria-busy={waiting} className="space-y-4">
            {shown.map((group) => <div key={group.kind} role="group" aria-labelledby={`global-search-group-${group.kind}`}>
              <h2 id={`global-search-group-${group.kind}`} className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--fg-muted)]">{t(`global_search.types.${group.kind}`)}</h2>
              <ul role="presentation" className="space-y-1">{group.hits.map((hit) => {
                const option = options.find((candidate) => candidate.kind === group.kind && candidate.id === hit.id)!;
                const selected = option.optionId === active?.optionId;
                // Options never take focus (ARIA combobox pattern): arrow keys and Enter
                // are handled on the input through aria-activedescendant.
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                return <li key={hit.id} id={option.optionId} role="option" aria-selected={selected}
                  onMouseDown={(event) => { event.preventDefault(); }}
                  onClick={() => { setActiveId(hit.id); inputRef.current?.focus(); }}
                  className={`cursor-pointer rounded-[var(--radius-md)] border p-3 ${selected ? "border-[var(--accent)] bg-[var(--bg-subtle)]" : "border-[var(--border-subtle)] bg-[var(--bg-surface)] hover:bg-[var(--bg-subtle)]"}`}>
                  <span className="block break-words font-medium text-[var(--fg-primary)]">{highlight(hit.title, trimmed)}</span>
                  {hit.snippet && <span className="mt-1 block break-words text-sm text-[var(--fg-secondary)]">{hit.snippet}</span>}
                </li>;
              })}</ul>
            </div>)}
          </div>
          {result.isError && <p role="alert" className="text-sm text-[var(--state-danger)]">{t("global_search.partial_error")}</p>}
          <p className="text-xs text-[var(--fg-muted)]">{t("global_search.keyboard_hint")}</p>
          <p className="text-xs text-[var(--fg-muted)]">{t("global_search.limit_hint")}</p>
        </div>
        <aside className="min-w-0 self-start rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--bg-subtle)] p-4" aria-label={t("global_search.preview")} aria-live="polite">
          {active ? <>
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--fg-muted)]">{t(`global_search.types.${active.kind}`)}</p>
            <h2 className="mt-1 break-words font-semibold text-[var(--fg-primary)]">{active.title}</h2>
            <p className="mt-2 break-words text-sm text-[var(--fg-secondary)]">{active.snippet || t("global_search.no_summary")}</p>
            {opensPage(active.url) && <Link to={active.url} onClick={() => { if (term !== "") remember(term); onNavigate?.(); }} className="mt-4 inline-flex rounded border border-[var(--border-default)] bg-[var(--bg-surface)] px-3 py-2 text-sm font-medium text-[var(--accent)] focus-visible:outline-2">{t("global_search.open_page")}</Link>}
          </> : <p className="text-sm text-[var(--fg-muted)]">{t("global_search.select_hint")}</p>}
        </aside>
      </div>}
    <p className="text-sm text-[var(--fg-secondary)]">{t("global_search.scope_hint")}</p>
  </div>;
}
export function GlobalSearchPage() {
  const { t } = useTranslation();
  useTabTitle(t("global_search.title"));
  const [params] = useSearchParams();
  const scope = useSessionScope();
  return <div className="space-y-5"><PageHeader title={t("global_search.title")} description={t("global_search.description")} />
    <RecordSearch key={JSON.stringify([...scope, params.get("q"), params.get("selected")])} initialQuery={params.get("q") ?? ""} initialSelected={params.get("selected") ?? ""} />
  </div>;
}
export function GlobalSearchTrigger() {
  const scope = useSessionScope();
  return <GlobalSearchTriggerBody key={JSON.stringify(scope)} />;
}
function GlobalSearchTriggerBody() {
  const { t } = useTranslation();
  const open = useUiStore((s) => s.globalSearchOpen);
  const setOpen = useUiStore((s) => s.setGlobalSearchOpen);
  // A previous session's open dialog must not reappear for the next identity.
  useEffect(() => () => { setOpen(false); }, [setOpen]);
  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k" && !event.altKey && !event.shiftKey && !event.isComposing) {
        // Üst üste dialog açmak arka plandaki focus trap'i erişilemez yapar.
        if (document.querySelector('[role="dialog"][aria-modal="true"]') && !open) return;
        event.preventDefault(); setOpen(!open);
      }
    };
    window.addEventListener("keydown", onKey); return () => window.removeEventListener("keydown", onKey);
  }, [open, setOpen]);
  return <><button type="button" onClick={() => setOpen(true)} aria-label={t("global_search.open")} aria-keyshortcuts="Meta+K Control+K" title={t("global_search.shortcut")}
    className="flex h-10 w-10 items-center justify-center rounded-lg text-[var(--fg-secondary)] hover:bg-[var(--bg-subtle)] focus-visible:outline-2 focus-visible:outline-[var(--focus-ring)]"><Search size={18} aria-hidden="true" /></button>
    <Modal open={open} onClose={() => setOpen(false)} aria-labelledby="global-search-title"><ModalHeader id="global-search-title" title={t("global_search.title")} /><ModalBody>
      {open && <RecordSearch onNavigate={() => setOpen(false)} />}
    </ModalBody></Modal></>;
}
